import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Table from '../common/Table';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/admin.css';

const EMPTY = {
  enabled: false,
  require_payment: true,
  environment: 'sandbox',
  consumer_key: '',
  consumer_secret: '',
  passkey: '',
  shortcode: '',
  paybill_number: '',
  account_prefix: 'HVQ',
  stk_callback_url: '',
  c2b_confirmation_url: '',
  c2b_validation_url: '',
  transaction_type: 'CustomerPayBillOnline'
};

const MpesaBilling = () => {
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({ configured: false, consumer_secret_set: false, passkey_set: false });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [querying, setQuerying] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, payRes] = await Promise.all([
        api.get('/admin/mpesa'),
        api.get('/admin/subscription-payments', { params: { limit: 30 } })
      ]);
      const c = cfgRes.data.config || {};
      setForm({
        enabled: !!c.enabled,
        require_payment: c.require_payment !== false,
        environment: c.environment || 'sandbox',
        consumer_key: c.consumer_key || '',
        consumer_secret: '',
        passkey: '',
        shortcode: c.shortcode || '',
        paybill_number: c.paybill_number || '',
        account_prefix: c.account_prefix || 'HVQ',
        stk_callback_url: c.stk_callback_url || '',
        c2b_confirmation_url: c.c2b_confirmation_url || '',
        c2b_validation_url: c.c2b_validation_url || '',
        transaction_type: c.transaction_type || 'CustomerPayBillOnline'
      });
      setMeta({
        configured: !!c.configured,
        consumer_secret_set: !!c.consumer_secret_set,
        passkey_set: !!c.passkey_set
      });
      setPayments(payRes.data.payments || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load M-Pesa settings');
    } finally {
      setLoading(false);
    }
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.consumer_secret) delete payload.consumer_secret;
      if (!payload.passkey) delete payload.passkey;
      const res = await api.put('/admin/mpesa', payload);
      toast.success(res.data.message || 'Saved');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTestStk = async () => {
    if (!testPhone) {
      toast.error('Enter a phone number');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/admin/mpesa/test-stk', { phone: testPhone });
      setTestResult(res.data);
      toast.success(res.data.message || 'STK sent');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Test STK failed');
    } finally {
      setTesting(false);
    }
  };

  const handleQueryStk = async () => {
    const checkoutId = testResult?.checkout_request_id || testResult?.daraja?.CheckoutRequestID;
    if (!checkoutId) {
      toast.error('Send a test STK first');
      return;
    }
    setQuerying(true);
    try {
      const res = await api.post('/admin/mpesa/stk-query', { checkout_request_id: checkoutId });
      setTestResult((prev) => ({ ...prev, query: res.data }));
      const desc = res.data?.daraja?.ResultDesc || res.data?.daraja?.ResponseDescription || 'Query complete';
      toast.success(desc);
    } catch (err) {
      toast.error(err.response?.data?.error || 'STK query failed');
    } finally {
      setQuerying(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading M-Pesa billing…</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>M-Pesa Paybill Billing</h1>
          <p>
            Platform Paybill for tenant app subscriptions. Tenants pay via STK Push or Lipa na M-Pesa Paybill;
            access unlocks only after a successful transaction.
          </p>
        </div>
        <span className={`status-pill ${meta.configured && form.enabled ? 'active' : 'inactive'}`}>
          {meta.configured && form.enabled ? 'Live ready' : 'Not active'}
        </span>
      </div>

      <form onSubmit={handleSave} className="admin-two-col" style={{ alignItems: 'start' }}>
        <Card title="Daraja / Paybill credentials">
          <label className="form-check">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setField('enabled', e.target.checked)}
            />
            Enable platform M-Pesa billing
          </label>
          <label className="form-check">
            <input
              type="checkbox"
              checked={form.require_payment}
              onChange={(e) => setField('require_payment', e.target.checked)}
            />
            Require payment before unlocking apps
          </label>

          <div className="form-group">
            <label>Environment</label>
            <select
              value={form.environment}
              onChange={(e) => setField('environment', e.target.value)}
            >
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </div>

          {form.environment === 'sandbox' && (
            <div className="help-box" style={{ marginBottom: '1rem' }}>
              <strong>Sandbox checklist</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
                <li>Shortcode &amp; Paybill: <code>174379</code> (auto-applied for STK)</li>
                <li>Passkey: copy from your Daraja sandbox app (Lipa Na M-Pesa Online)</li>
                <li>Test phone: <code>254708374149</code> — enter any PIN on the prompt</li>
                <li>Transaction type is forced to Paybill Online in sandbox</li>
              </ul>
            </div>
          )}

          {form.environment === 'production' && (
            <div className="form-group">
              <label>Transaction type</label>
              <select
                value={form.transaction_type}
                onChange={(e) => setField('transaction_type', e.target.value)}
              >
                <option value="CustomerPayBillOnline">Paybill (PartyB = paybill number)</option>
                <option value="CustomerBuyGoodsOnline">Till / Buy Goods (PartyB = shortcode)</option>
              </select>
            </div>
          )}

          <Input
            label="Consumer Key"
            value={form.consumer_key}
            onChange={(e) => setField('consumer_key', e.target.value)}
          />
          <Input
            label={`Consumer Secret${meta.consumer_secret_set ? ' (leave blank to keep)' : ''}`}
            type="password"
            value={form.consumer_secret}
            onChange={(e) => setField('consumer_secret', e.target.value)}
            placeholder={meta.consumer_secret_set ? '••••••••' : ''}
          />
          <Input
            label={`Passkey${meta.passkey_set ? ' (leave blank to keep)' : ''}`}
            type="password"
            value={form.passkey}
            onChange={(e) => setField('passkey', e.target.value)}
            placeholder={meta.passkey_set ? '••••••••' : ''}
          />
          <Input
            label="Business Shortcode (Lipa Na M-Pesa Online)"
            value={form.shortcode}
            onChange={(e) => setField('shortcode', e.target.value)}
            placeholder={form.environment === 'sandbox' ? '174379' : 'From Safaricom go-live / Daraja'}
          />
          <Input
            label="Paybill Number (PartyB — shown to tenants)"
            value={form.paybill_number}
            onChange={(e) => setField('paybill_number', e.target.value)}
            placeholder={form.environment === 'sandbox' ? '174379' : 'Your customer-facing paybill'}
          />
          {form.environment === 'production' && (
            <p className="help-text">
              For Paybill STK: <strong>Shortcode</strong> is the Lipa Na M-Pesa Online shortcode (often different from
              the paybill). <strong>Paybill</strong> is PartyB — where money lands. If prompts never arrive but Daraja
              returns success, these two are usually swapped.
            </p>
          )}
          <Input
            label="Account reference prefix"
            value={form.account_prefix}
            onChange={(e) => setField('account_prefix', e.target.value)}
            placeholder="HVQ"
          />
          <p className="help-text">
            Tenant account number = prefix + business ID (example: HVQ12). Customers enter this as Bill Reference.
          </p>

          <Button type="submit" variant="primary" loading={saving}>
            Save M-Pesa settings
          </Button>
        </Card>

        <div>
          <Card title="Callback URLs (register in Daraja)">
            <Input
              label="STK Callback URL"
              value={form.stk_callback_url}
              onChange={(e) => setField('stk_callback_url', e.target.value)}
            />
            <Input
              label="C2B Validation URL"
              value={form.c2b_validation_url}
              onChange={(e) => setField('c2b_validation_url', e.target.value)}
            />
            <Input
              label="C2B Confirmation URL"
              value={form.c2b_confirmation_url}
              onChange={(e) => setField('c2b_confirmation_url', e.target.value)}
            />
            <p className="help-text">
              Register these URLs in the Safaricom Daraja portal (STK + C2B). Confirmation unlocks apps when
              tenants pay via Paybill manually.
            </p>
          </Card>

          <Card title="Test STK Push (KES 1)">
            <Input
              label="Phone (07… or 2547…)"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder={form.environment === 'sandbox' ? '254708374149' : ''}
            />
            {form.environment === 'sandbox' && (
              <p className="help-text">
                Use Safaricom sandbox test number <code>254708374149</code>. Real numbers will not receive a prompt.
              </p>
            )}
            <Button type="button" variant="secondary" loading={testing} onClick={handleTestStk}>
              Send test prompt
            </Button>
            {testResult?.checkout_request_id && (
              <Button type="button" variant="secondary" loading={querying} onClick={handleQueryStk} style={{ marginLeft: '0.5rem' }}>
                Check STK status
              </Button>
            )}
            {testResult?.routing && (
              <div className="help-box" style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                <strong>Last test routing</strong>
                <div>Environment: <code>{testResult.routing.environment}</code></div>
                <div>BusinessShortCode: <code>{testResult.routing.business_short_code}</code></div>
                <div>PartyB: <code>{testResult.routing.party_b}</code></div>
                <div>Transaction type: <code>{testResult.routing.transaction_type}</code></div>
                <div>Phone sent: <code>{testResult.phone}</code></div>
                {testResult.routing.sandbox_forced && (
                  <div style={{ marginTop: '0.5rem' }}>Sandbox mode forces shortcode/PartyB to 174379.</div>
                )}
                {testResult.query?.daraja && (
                  <div style={{ marginTop: '0.5rem' }}>
                    Query: <code>{testResult.query.daraja.ResultDesc || testResult.query.daraja.ResponseDescription}</code>
                    {testResult.query.daraja.ResultCode != null && (
                      <> (code {String(testResult.query.daraja.ResultCode)})</>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </form>

      <Card title="Recent subscription payments" className="mt-3">
        <Table
          columns={[
            { key: 'id', label: 'ID' },
            { key: 'business_name', label: 'Business' },
            {
              key: 'amount',
              label: 'Amount',
              render: (value) => formatCurrency(value)
            },
            { key: 'account_reference', label: 'Account' },
            { key: 'payment_method', label: 'Method' },
            { key: 'status', label: 'Status' },
            { key: 'mpesa_receipt', label: 'Receipt' },
            {
              key: 'created_at',
              label: 'Created',
              render: (value) => formatDate(value)
            }
          ]}
          data={payments}
          emptyMessage="No subscription payments yet"
        />
      </Card>
    </div>
  );
};

export default MpesaBilling;
